import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchBulkRoster, bulkSaveAttendance, todayISO } from './attendanceApi';
import ClassAccordion, { TAP } from './ClassAccordion';
import WhatsAppDispatcher from './WhatsAppDispatcher';
import BodyPortal from './BodyPortal';

const Skeleton = () => (
  <div aria-busy="true">
    <style>{'@keyframes qmPulse{0%,100%{opacity:1}50%{opacity:.45}}'}</style>
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <div key={i} style={{ height: 60, borderRadius: 12, background: '#e5e7eb', marginBottom: 10, animation: 'qmPulse 1.2s infinite' }} />
    ))}
  </div>
);

// Mobile-first single page: all classes in accordions + sticky "Save All Attendance" bar.
// After the bulk save it switches to the WhatsApp Dispatcher queue.
export default function QuickMarkDashboard({ user, onClose }) {
  const [date, setDate] = useState(todayISO());
  const [classes, setClasses] = useState(null);
  const [absent, setAbsent] = useState({});     // { [classId]: studentId[] }
  const [openId, setOpenId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [stage, setStage] = useState('mark');   // 'mark' | 'dispatch'
  const [savedClasses, setSavedClasses] = useState([]);

  const load = useCallback(async () => {
    setClasses(null); setMsg('');
    try {
      const r = await fetchBulkRoster(user, date);
      setClasses(r.classes);
      setAbsent(Object.fromEntries(r.classes.map((c) => [c.classId, c.absentStudentIds])));
      setDirty(false);
    } catch (e) { setClasses([]); setMsg(e.response?.data?.error || 'Could not load classes'); }
  }, [user, date]);

  useEffect(() => { load(); }, [load]);

  const add = (cid, id) => { setAbsent((a) => ({ ...a, [cid]: [...(a[cid] || []), id] })); setDirty(true); };
  const remove = (cid, id) => { setAbsent((a) => ({ ...a, [cid]: (a[cid] || []).filter((x) => x !== id) })); setDirty(true); };

  const totalAbsent = useMemo(() => Object.values(absent).reduce((n, l) => n + l.length, 0), [absent]);

  const saveAll = async () => {
    const active = (classes || []).filter((c) => !c.holiday);
    if (!active.length) { setMsg('No classes to save for this date.'); return; }
    setSaving(true); setMsg('');
    try {
      const payload = active.map((c) => ({ classId: c.classId, date, absentStudentIds: absent[c.classId] || [] }));
      const r = await bulkSaveAttendance(user, payload);
      setSavedClasses(r.results.filter((x) => x.status === 'saved').map((x) => x.classId));
      setDirty(false); setStage('dispatch');
    } catch (e) {
      setMsg(e.response?.data?.error || 'Save failed. Nothing was saved, please retry.');
    } finally { setSaving(false); }
  };

  if (stage === 'dispatch') {
    return (
      <BodyPortal>
        <WhatsAppDispatcher user={user} date={date} classIds={savedClasses}
          onBack={() => { setStage('mark'); load(); }} onClose={onClose} />
      </BodyPortal>
    );
  }

  const ok = () => !dirty || window.confirm('Discard unsaved changes?');

  return (
    <BodyPortal>
    <div className="caf-theme-wrapper min-h-[100dvh] fixed inset-0 bg-gray-100 z-[9000] overflow-y-auto overscroll-none"
      style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, minHeight: '100dvh', overflowY: 'auto', background: '#f3f4f6' }}>
      <div className="w-full max-w-xl md:max-w-5xl mx-auto p-3 md:p-6 pb-32" style={{ paddingBottom: 112 }}>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-xl font-bold flex-1">⚡ Quick-Mark Attendance</h2>
          <button onClick={() => ok() && onClose()} aria-label="Close"
            className="w-10 h-10 bg-gray-200 rounded-xl text-xl font-bold hover:bg-gray-300">✕</button>
        </div>

        <input type="date" value={date} max={todayISO()}
          onChange={(e) => { if (e.target.value && ok()) setDate(e.target.value); }}
          className="w-full md:w-72 box-border min-h-10 px-3 text-base rounded-lg border-2 border-gray-300 mb-3" />

        {classes === null && <div style={{ textAlign: 'center', padding: 8, color: '#4b5563', fontSize: 14 }}>Loading classes…</div>}
        {classes === null && <Skeleton />}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 items-start">
          {classes && classes.map((c) => (
            <ClassAccordion key={c.classId} cls={c} open={openId === c.classId}
              onToggle={() => setOpenId(openId === c.classId ? null : c.classId)}
              absentIds={absent[c.classId] || []}
              onAdd={(id) => add(c.classId, id)} onRemove={(id) => remove(c.classId, id)} />
          ))}
        </div>
        {classes && classes.length === 0 && !msg && <div className="text-center text-gray-600 py-4" style={{ textAlign: 'center', padding: 16, color: '#4b5563' }}>No classes found.</div>}
        {msg && <div role="alert" className="mt-2 text-sm text-red-700">{msg}</div>}
      </div>

      {/* Global sticky action bar */}
      <div className="fixed left-0 right-0 bottom-0 z-[9100] bg-white border-t border-gray-200 p-3 pb-[calc(12px+env(safe-area-inset-bottom))] shadow-lg"
        style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 9100, background: '#fff', borderTop: '1px solid #e5e7eb', padding: '12px 12px calc(12px + env(safe-area-inset-bottom))' }}>
        <div className="max-w-xl md:max-w-md mx-auto">
          <button onClick={saveAll} disabled={saving || !classes || !classes.length}
            className={`w-full min-h-14 rounded-2xl text-lg font-extrabold text-white transition-colors duration-200 ${saving ? 'bg-gray-400' : dirty ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {saving ? 'Saving…' : `💾 Save All Attendance${totalAbsent ? ` (${totalAbsent} absent)` : ''}`}
          </button>
        </div>
      </div>
    </div>
    </BodyPortal>
  );
}
